<script>
	import { onMount, onDestroy } from 'svelte';
	import Chart from 'chart.js/auto';
	import 'chartjs-adapter-date-fns';
	import { createGradient, baseChartOptions, timeXAxis, valueYAxis, darkTooltip, COLORS } from './ChartTheme.js';

	export let sleepwaveLights = [];
	export let samsungLights = [];
	export let title = '조명 제어 이력';

	let chartCanvas;
	let chart;

	function parseLightControls(lights, isSamsung) {
		const points = [];
		for (const lightGroup of lights) {
			for (const ctrl of lightGroup.controls) {
				let brightness, colorTemp, power, time;
				if (isSamsung) {
					time = ctrl.timestamp;
					brightness = parseInt(ctrl.brightness) || 0;
					colorTemp = parseInt(ctrl.colorTemp) || 0;
					power = ctrl.power;
				} else {
					time = new Date(ctrl.timestamp).getTime();
					try {
						const raw = JSON.parse(ctrl.rawData);
						brightness = parseInt(raw.brightness) || 0;
						colorTemp = parseInt(raw.colorTemp || raw.color_temp) || 0;
						power = raw.power;
					} catch {
						brightness = 0;
						colorTemp = 0;
						power = 'off';
					}
				}
				points.push({ time, brightness, colorTemp, power });
			}
		}
		return points.sort((a, b) => a.time - b.time);
	}

	function createChart() {
		if (chart) chart.destroy();
		if (!chartCanvas) return;

		const datasets = [];
		const swPoints = parseLightControls(sleepwaveLights, false);
		const ssPoints = parseLightControls(samsungLights, true);

		if (swPoints.length > 0) {
			datasets.push({
				label: 'SW 밝기',
				data: swPoints.map(d => ({ x: d.time, y: d.brightness })),
				borderColor: '#6366f1',
				backgroundColor: function(context) {
					const { ctx, chartArea } = context.chart;
					return createGradient(ctx, chartArea, '#6366f1', '#6366f1', 0.15, 0.02);
				},
				borderWidth: 2,
				pointRadius: 3,
				pointBackgroundColor: '#6366f1',
				pointBorderColor: '#fff',
				pointBorderWidth: 1.5,
				pointHoverRadius: 5,
				stepped: 'after',
				fill: true,
				yAxisID: 'y',
			});
			datasets.push({
				label: 'SW 색온도',
				data: swPoints.filter(d => d.colorTemp > 0).map(d => ({ x: d.time, y: d.colorTemp })),
				borderColor: '#f59e0b',
				borderWidth: 1.5,
				pointRadius: 2,
				pointBackgroundColor: '#f59e0b',
				pointBorderColor: '#fff',
				pointBorderWidth: 1,
				stepped: 'after',
				fill: false,
				yAxisID: 'y1',
			});
		}

		if (ssPoints.length > 0) {
			datasets.push({
				label: 'Samsung 밝기',
				data: ssPoints.map(d => ({ x: d.time, y: d.brightness })),
				borderColor: '#f472b6',
				backgroundColor: function(context) {
					const { ctx, chartArea } = context.chart;
					return createGradient(ctx, chartArea, '#f472b6', '#f472b6', 0.1, 0.01);
				},
				borderWidth: 2,
				pointRadius: 3,
				pointBackgroundColor: '#f472b6',
				pointBorderColor: '#fff',
				pointBorderWidth: 1.5,
				stepped: 'after',
				borderDash: [6, 4],
				fill: true,
				yAxisID: 'y',
			});
			datasets.push({
				label: 'Samsung 색온도',
				data: ssPoints.filter(d => d.colorTemp > 0).map(d => ({ x: d.time, y: d.colorTemp })),
				borderColor: '#fb923c',
				borderWidth: 1.5,
				pointRadius: 2,
				pointBackgroundColor: '#fb923c',
				pointBorderColor: '#fff',
				pointBorderWidth: 1,
				stepped: 'after',
				borderDash: [4, 3],
				fill: false,
				yAxisID: 'y1',
			});
		}

		if (datasets.length === 0) return;

		const hasColorTemp = datasets.some(d => d.yAxisID === 'y1' && d.data.length > 0);

		chart = new Chart(chartCanvas, {
			type: 'line',
			data: { datasets },
			options: {
				...baseChartOptions(),
				plugins: {
					legend: {
						display: true,
						position: 'top',
						align: 'end',
						labels: {
							color: COLORS.textSecondary,
							font: { size: 11 },
							boxWidth: 12,
							boxHeight: 2,
							padding: 12,
							usePointStyle: false,
						}
					},
					tooltip: {
						...darkTooltip,
						callbacks: {
							title: items => {
								if (items.length > 0) {
									return new Date(items[0].parsed.x).toLocaleTimeString('ko-KR', {
										hour: '2-digit', minute: '2-digit', second: '2-digit'
									});
								}
								return '';
							},
							labelColor: function(ctx) {
								return {
									borderColor: ctx.dataset.borderColor,
									backgroundColor: ctx.dataset.borderColor,
									borderWidth: 2,
									borderRadius: 2,
								};
							}
						}
					}
				},
				scales: {
					x: timeXAxis(),
					y: valueYAxis({
						position: 'left',
						min: 0,
						max: 100,
						ticks: {
							color: COLORS.textSecondary,
							font: { size: 11 },
							padding: 6,
							callback: val => `${val}%`,
						},
						grid: {
							color: COLORS.gridLine,
							drawBorder: false,
							tickLength: 0,
						},
						title: { display: true, text: '밝기', color: COLORS.textMuted, font: { size: 11 } },
					}),
					y1: valueYAxis({
						position: 'right',
						display: hasColorTemp,
						grid: { drawOnChartArea: false, display: false },
						ticks: {
							color: '#fb923c',
							font: { size: 11 },
							padding: 6,
						},
						title: { display: true, text: '색온도 (K)', color: '#fb923c', font: { size: 11 } },
					}),
				}
			}
		});
	}

	$: if (chartCanvas && (sleepwaveLights || samsungLights)) {
		createChart();
	}

	onMount(() => {
		createChart();
	});

	onDestroy(() => {
		if (chart) chart.destroy();
	});
</script>

<div class="chart-card">
	<div class="chart-header">
		<h5 class="chart-title">{title}</h5>
	</div>
	<div class="chart-body">
		{#if sleepwaveLights.length === 0 && samsungLights.length === 0}
			<div class="empty-state">조명 제어 데이터가 없습니다</div>
		{:else}
			<div class="chart-container">
				<canvas bind:this={chartCanvas}></canvas>
			</div>
		{/if}
	</div>
</div>

<style>
	.chart-card {
		background: #ffffff;
		border: 1px solid #e2e8f0;
		border-radius: 16px;
		padding: 20px 24px 16px;
		margin-bottom: 20px;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
	}
	.chart-header {
		margin-bottom: 12px;
	}
	.chart-title {
		font-size: 15px;
		font-weight: 600;
		color: #334155;
		margin: 0;
	}
	.chart-body {
		position: relative;
	}
	.chart-container {
		height: 260px;
	}
	.empty-state {
		text-align: center;
		color: #94a3b8;
		padding: 48px 0;
		font-size: 14px;
	}
</style>
